import { useMemo } from 'react';
import { Box, Button, useTheme } from '@mui/material';
import { CampaignFilterCategory } from '@prisma/client';
import CloseRounded from '@mui/icons-material/CloseRounded';
import SingleSelectControlled from '../../../form/base/SingleSelectControlled';
import { CampaignFilter } from '../../../../types/marketing';
import {
  AppointmentServiceInput,
  CountInputs,
  DateInputs,
  EmployeeSelectInput,
  inputContainerStyle,
  SpendInputs,
} from './inputs';
import useUser from '../../../../hooks/global/useUser';

interface Props {
  filter: CampaignFilter;
  onChange: (filter: Partial<CampaignFilter>) => void;
  onDelete?: () => void;
}

function SegmentFilter({ filter, onChange, onDelete }: Props) {
  const theme = useTheme();
  const { user } = useUser();

  const category =
    filter.category === 'LAST_APPOINTMENT_DATE'
      ? 'LAST_APPOINTMENT_WITHIN_DAYS'
      : filter.category;

  const options = useMemo(() => {
    const initialOptions = [
      { value: 'SPEND', label: 'Total spend' },
      {
        value: 'LAST_APPOINTMENT_WITHIN_DAYS',
        label: 'Last appointment visit',
      },
      {
        value: 'LAST_APPOINTMENT_DATE',
        label: 'Last appointment visit',
        hidden: true,
      },
      { value: 'APPOINTMENT_COUNT', label: 'Number of visits' },
      { value: 'APPOINTMENT_SERVICE', label: 'Is a client for the service' },
    ];

    if (user?.business?.type === 'STUDIO') {
      initialOptions.push({
        value: 'EMPLOYEE_RELATION',
        label: 'Is on the mailing list for',
      });
    }

    return initialOptions;
  }, [user?.business?.type]);

  return (
    <Box
      overflow="auto"
      bgcolor={theme.palette.lightGrey.main}
      borderRadius="20px"
      py={2}
      px={2.5}
      {...inputContainerStyle}>
      <SingleSelectControlled
        id="category"
        placeholder="Select a filter"
        options={options}
        onChange={value => {
          onChange({
            category: value as CampaignFilterCategory,
            value: '' as any,
            operator: 'GT',
          });
        }}
        value={category}
        autoWidth={false}
      />

      {category === 'SPEND' && (
        <SpendInputs filter={filter} onChange={onChange} />
      )}
      {category === 'LAST_APPOINTMENT_WITHIN_DAYS' && (
        <DateInputs filter={filter} onChange={onChange} />
      )}
      {category === 'APPOINTMENT_COUNT' && (
        <CountInputs filter={filter} onChange={onChange} />
      )}
      {category === 'EMPLOYEE_RELATION' && (
        <EmployeeSelectInput filter={filter} onChange={onChange} />
      )}
      {category === 'APPOINTMENT_SERVICE' && (
        <AppointmentServiceInput filter={filter} onChange={onChange} />
      )}

      {onDelete && (
        <Button
          variant="text"
          sx={{
            minWidth: 0,
            ml: 'auto',
            aspectRatio: '1/1',
          }}
          onClick={onDelete}>
          <CloseRounded
            sx={{
              width: '16px',
              height: '16px',
            }}
            htmlColor={theme.palette.darkGrey.main}
          />
        </Button>
      )}
    </Box>
  );
}

export default SegmentFilter;
